import { useEffect, type ReactNode } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import Link from "@tiptap/extension-link";
import { Markdown } from "tiptap-markdown";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type RichTextToolbarMode = "full" | "basic" | "document";

type Props = {
  initialMarkdown: string;
  onChangeMarkdown: (markdown: string) => void;
  className?: string;
  showToolbar?: boolean;
  toolbarMode?: RichTextToolbarMode;
  editorMinHeightClass?: string;
  /** Drop the outer frame when the editor sits inside a split panel. */
  flush?: boolean;
};

function ToolbarButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? "default" : "secondary"}
      aria-label={label}
      aria-pressed={active}
      className="h-8 min-w-8 px-2"
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

export default function RichTextEditor({
  initialMarkdown,
  onChangeMarkdown,
  className,
  showToolbar = true,
  toolbarMode = "full",
  editorMinHeightClass,
  flush = false,
}: Props) {
  const FontSize = Extension.create({
    name: "fontSize",
    addGlobalAttributes() {
      return [
        {
          types: ["textStyle"],
          attributes: {
            fontSize: {
              default: null,
              parseHTML: (element) => {
                const size = (element as HTMLElement).style.fontSize;
                return size || null;
              },
              renderHTML: (attributes: Record<string, unknown>) => {
                const fontSize = attributes.fontSize;
                if (!fontSize || typeof fontSize !== "string") return {};
                return { style: `font-size: ${fontSize}` };
              },
            },
          },
        },
      ];
    },
  });

  const defaultEditorMinHeightClass =
    toolbarMode === "full" ? "min-h-[400px]" : toolbarMode === "document" ? "min-h-[320px]" : "min-h-[240px]";
  const resolvedEditorMinHeightClass = editorMinHeightClass ?? defaultEditorMinHeightClass;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: true }),
      Underline,
      TextStyle,
      FontFamily,
      Link.configure({ openOnClick: false }),
      FontSize,
      Markdown.configure({
        html: false,
      }),
    ],
    content: initialMarkdown || "",
    onUpdate: ({ editor: currentEditor }) => {
      const md = (currentEditor.storage as { markdown?: { getMarkdown: () => string } }).markdown?.getMarkdown();
      if (typeof md === "string") onChangeMarkdown(md);
    },
    editorProps: {
      attributes: {
        class: cn("prose dark:prose-invert max-w-none focus:outline-none", resolvedEditorMinHeightClass),
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const storage = editor.storage as { markdown?: { getMarkdown: () => string } };
    const current = storage.markdown?.getMarkdown?.();
    if (typeof current === "string" && current.trim() === (initialMarkdown || "").trim()) {
      return;
    }
    editor.commands.setContent(initialMarkdown || "");
  }, [editor, initialMarkdown]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Enter URL", previousUrl ?? "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const basicToolbar = (
    <div className="flex flex-wrap items-center gap-1.5">
      <ToolbarButton
        label="Bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <span className="font-semibold">B</span>
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <span className="italic">I</span>
      </ToolbarButton>
      <ToolbarButton
        label="Heading 2"
        active={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        H2
      </ToolbarButton>
      <ToolbarButton
        label="Bullet list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        • List
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        1. List
      </ToolbarButton>
    </div>
  );

  const documentToolbar = (
    <div className="flex flex-wrap items-center gap-1.5">
      <ToolbarButton
        label="Bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <span className="font-semibold">B</span>
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <span className="italic">I</span>
      </ToolbarButton>
      <ToolbarButton
        label="Underline"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <span className="underline">U</span>
      </ToolbarButton>
      <span className="mx-1 hidden h-5 w-px bg-border sm:inline" aria-hidden />
      <ToolbarButton
        label="Heading 2"
        active={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        H2
      </ToolbarButton>
      <ToolbarButton
        label="Heading 3"
        active={editor.isActive("heading", { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        H3
      </ToolbarButton>
      <span className="mx-1 hidden h-5 w-px bg-border sm:inline" aria-hidden />
      <ToolbarButton
        label="Bullet list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        • List
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        1. List
      </ToolbarButton>
      <ToolbarButton
        label="Block quote"
        active={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        Quote
      </ToolbarButton>
    </div>
  );

  const fullToolbar = (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <ToolbarButton
        label="Bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <span className="font-semibold">B</span>
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <span className="italic">I</span>
      </ToolbarButton>
      <ToolbarButton
        label="Underline"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <span className="underline">U</span>
      </ToolbarButton>
      <ToolbarButton
        label="Heading 1"
        active={editor.isActive("heading", { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        H1
      </ToolbarButton>
      <ToolbarButton
        label="Heading 2"
        active={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        H2
      </ToolbarButton>
      <ToolbarButton
        label="Bullet list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        • List
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        1. List
      </ToolbarButton>
      <ToolbarButton label="Add link" active={editor.isActive("link")} onClick={setLink}>
        Link
      </ToolbarButton>
      <ToolbarButton label="Remove link" active={false} onClick={() => editor.chain().focus().unsetLink().run()}>
        Unlink
      </ToolbarButton>
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Font"
          className="h-8 w-44 shrink-0 rounded-md border bg-background pl-2 pr-8 text-sm"
          onChange={(e) => {
            const family = e.target.value;
            if (!family) {
              editor.chain().focus().unsetFontFamily().run();
            } else {
              editor.chain().focus().setFontFamily(family).run();
            }
          }}
        >
          <option value="">Font</option>
          <option value="system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif">System</option>
          <option value="Georgia, 'Times New Roman', serif">Serif</option>
          <option value="Arial, Helvetica, sans-serif">Arial</option>
          <option value="'Times New Roman', Times, serif">Times New Roman</option>
          <option value="'Courier New', Courier, monospace">Courier New</option>
        </select>
        <select
          aria-label="Font size"
          className="h-8 w-24 shrink-0 rounded-md border bg-background pl-2 pr-8 text-sm"
          onChange={(e) => {
            const v = e.target.value;
            if (!v) {
              editor.chain().focus().setMark("textStyle", { fontSize: null }).run();
            } else {
              const size = /px$/.test(v) ? v : `${v}px`;
              editor.chain().focus().setMark("textStyle", { fontSize: size }).run();
            }
          }}
        >
          <option value="">Size</option>
          <option value="12">12</option>
          <option value="14">14</option>
          <option value="16">16</option>
          <option value="18">18</option>
          <option value="20">20</option>
          <option value="24">24</option>
        </select>
      </div>
    </div>
  );

  const toolbar =
    showToolbar && (toolbarMode === "full" ? fullToolbar : toolbarMode === "document" ? documentToolbar : basicToolbar);
  const usePinnedToolbar = showToolbar && (toolbarMode === "basic" || toolbarMode === "document");

  if (usePinnedToolbar) {
    return (
      <div
        className={cn(
          className,
          "flex min-h-0 flex-col overflow-hidden bg-background",
          flush ? "rounded-none border-0" : "rounded-md border",
        )}
      >
        <div className="shrink-0 border-b bg-muted/20 px-3 py-2">{toolbar}</div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          <EditorContent editor={editor} />
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {toolbar}
      <div className={cn("h-full rounded-md border p-3", resolvedEditorMinHeightClass)}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
